
import { universalStorage } from '../../storage/universalStorage';
import { v4 as uuidv4 } from 'uuid';

export interface Timetable {
  id: string;
  title: string;
  description?: string;
  type: 'Class' | 'Exam' | 'Meeting' | 'Event';
  startTime: string;
  endTime: string;
  date: string;
  room?: string;
  teacher?: string;
  students: string[];
  subject?: string;
  status: 'Scheduled' | 'In Progress' | 'Completed' | 'Cancelled';
  recurring?: {
    type: 'Daily' | 'Weekly' | 'Monthly';
    endDate?: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
  syncStatus?: 'synced' | 'pending' | 'offline';
  lastModified?: string;
}

// Legacy interfaces for backward compatibility
export interface TimetableConfig {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  entries: TimetableEntry[];
  documents: string[];
  createdAt: string;
  updatedAt: string;
}

export interface TimetableEntry {
  id: string;
  day: string;
  startTime: string;
  endTime: string;
  subject: string;
  teacherId: string;
  room: string;
  studentGroup: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

class TimetableFileStorageService {
  private cache: Timetable[] | null = null;
  private readonly STORAGE_KEY = 'nalc_timetables';
  
  async getAllTimetables(): Promise<Timetable[]> {
    try {
      // Check cache first for performance
      if (this.cache && Array.isArray(this.cache)) {
        console.log(`📅 Returning ${this.cache.length} timetables from cache`);
        return this.cache;
      }

      console.log('📅 Loading timetables with localStorage-only backend (5000+ capacity)...');
      
      const timetables = await universalStorage.getItem<Timetable[]>(this.STORAGE_KEY) || [];
      
      // Ensure we always return an array and validate data structure
      let validTimetables: Timetable[] = [];
      
      if (Array.isArray(timetables)) {
        validTimetables = timetables.filter(timetable => {
          return timetable && 
                 typeof timetable === 'object' && 
                 timetable.id && 
                 typeof timetable.id === 'string';
        }).map(timetable => ({
          ...timetable,
          students: Array.isArray(timetable.students) ? timetable.students : [],
          createdAt: timetable.createdAt || new Date().toISOString(),
          updatedAt: timetable.updatedAt || new Date().toISOString(),
          syncStatus: timetable.syncStatus || 'synced'
        }));
      }
      
      // Cache the validated timetables
      this.cache = validTimetables;
      
      if (validTimetables.length > 0) {
        console.log(`✅ Loaded ${validTimetables.length} timetables from localStorage`);
      } else {
        console.log('📅 No timetables found, returning empty array');
      }
      
      return validTimetables;
    } catch (error) {
      console.error('Error loading timetables from localStorage:', error);
      console.log('📅 Initializing empty timetables storage');
      this.cache = [];
      return [];
    }
  }
  
  async getTimetableById(id: string): Promise<Timetable | null> {
    try {
      console.log(`🔍 getTimetableById called with ID: "${id}"`);
      
      const timetables = await this.getAllTimetables();
      
      if (!Array.isArray(timetables)) {
        console.error('getAllTimetables did not return an array');
        return null;
      }
      
      const timetable = timetables.find(timetable => timetable.id === id);
      
      if (timetable) {
        console.log(`✅ Found timetable: ${timetable.title} (ID: ${id})`);
      } else {
        console.log(`❌ Timetable not found with ID: ${id}`);
      }
      
      return timetable || null;
    } catch (error) {
      console.error(`Error loading timetable ${id}:`, error);
      return null;
    }
  }
  
  async addTimetable(timetableData: Omit<Timetable, 'id' | 'createdAt' | 'updatedAt'>): Promise<Timetable> {
    try {
      console.log('📅 Adding timetable with localStorage-only backend...');
      
      const timetables = await this.getAllTimetables();
      const now = new Date().toISOString();
      
      const newTimetable: Timetable = {
        ...timetableData,
        id: uuidv4(),
        createdAt: now,
        updatedAt: now,
        lastModified: now,
        students: Array.isArray(timetableData.students) ? timetableData.students : [],
        syncStatus: 'synced'
      };
      
      const updatedTimetables = [...timetables, newTimetable];
      
      await universalStorage.setItem(this.STORAGE_KEY, updatedTimetables);
      this.clearCache();
      
      console.log(`✅ Timetable added successfully: ${newTimetable.title} (Total: ${updatedTimetables.length})`);
      
      return newTimetable;
    } catch (error) {
      console.error('Error adding timetable:', error);
      throw error;
    }
  }
  
  async updateTimetable(id: string, timetableData: Partial<Timetable>): Promise<Timetable | null> {
    try {
      console.log(`📅 Updating timetable ${id} with localStorage-only backend...`);
      
      const timetables = await this.getAllTimetables();
      const timetableIndex = timetables.findIndex(timetable => timetable.id === id);
      
      if (timetableIndex === -1) {
        console.log(`❌ Timetable not found for update: ID ${id}`);
        return null;
      }
      
      const updatedTimetable: Timetable = {
        ...timetables[timetableIndex],
        ...timetableData,
        id: id, // Ensure ID doesn't change
        updatedAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        syncStatus: 'synced'
      };
      
      timetables[timetableIndex] = updatedTimetable;
      
      await universalStorage.setItem(this.STORAGE_KEY, timetables);
      this.clearCache();
      
      console.log(`✅ Timetable updated successfully: ${updatedTimetable.title}`);
      
      return updatedTimetable;
    } catch (error) {
      console.error(`Error updating timetable ${id}:`, error);
      throw error;
    }
  }
  
  async deleteTimetable(id: string): Promise<void> {
    try {
      console.log(`📅 Deleting timetable ${id}...`);
      
      const timetables = await this.getAllTimetables();
      const filteredTimetables = timetables.filter(timetable => timetable.id !== id);
      
      if (filteredTimetables.length === timetables.length) {
        console.log(`❌ Timetable not found for deletion: ID ${id}`);
        return;
      }
      
      await universalStorage.setItem(this.STORAGE_KEY, filteredTimetables);
      this.clearCache();
      
      console.log(`✅ Timetable deleted successfully (${filteredTimetables.length} timetables remaining)`);
    } catch (error) {
      console.error(`Error deleting timetable ${id}:`, error);
      throw error;
    }
  }

  clearCache(): void {
    this.cache = null;
  }

  async getStorageStats(): Promise<{
    totalTimetables: number;
    storageUsed: string;
    maxCapacity: number;
    isOptimized: boolean;
    compressionRatio?: number;
  }> {
    try {
      const timetables = await this.getAllTimetables();
      const data = await universalStorage.getItem(this.STORAGE_KEY) || '[]';
      const dataString = typeof data === 'string' ? data : JSON.stringify(data);
      const sizeInBytes = new Blob([dataString]).size;
      const sizeInKB = (sizeInBytes / 1024).toFixed(2);
      
      return {
        totalTimetables: Array.isArray(timetables) ? timetables.length : 0,
        storageUsed: `${sizeInKB} KB`,
        maxCapacity: 5000,
        isOptimized: true,
        compressionRatio: 1.0
      };
    } catch (error) {
      console.error('Error getting timetable storage stats:', error);
      return {
        totalTimetables: 0,
        storageUsed: '0 KB',
        maxCapacity: 5000,
        isOptimized: false
      };
    }
  }

  async batchAddTimetables(timetables: Omit<Timetable, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<Timetable[]> {
    try {
      console.log(`📅 Batch adding ${timetables.length} timetables...`);
      
      const existingTimetables = await this.getAllTimetables();
      const now = new Date().toISOString();
      
      const newTimetables = timetables.map(timetable => ({
        ...timetable,
        id: uuidv4(),
        createdAt: now,
        updatedAt: now,
        lastModified: now,
        students: Array.isArray(timetable.students) ? timetable.students : [],
        syncStatus: 'synced' as const
      }));

      const allTimetables = [...existingTimetables, ...newTimetables];
      await universalStorage.setItem(this.STORAGE_KEY, allTimetables);
      this.clearCache();

      console.log(`✅ Batch added ${newTimetables.length} timetables successfully`);
      return newTimetables;
    } catch (error) {
      console.error('Error batch adding timetables:', error);
      throw error;
    }
  }
}

export const timetableFileStorageService = new TimetableFileStorageService();
